import React from 'react';
import { VUKZ } from '../../theme/vukzTheme';

/**
 * PageHeader — Official VUKZ Page Header (v2)
 *
 * THE single shared header for every page in the app.
 * Replaces both the old PageHeader and BrandPageHeader.
 *
 * Layout:
 *   ┌─────────────────────────────────────────────────────────┐
 *   │ [Logo] | Title                        [actions/rightSlot]│
 *   │          Subtitle · badgeText                            │
 *   │          [secondaryContent]                              │
 *   └─────────────────────────────────────────────────────────┘
 *
 * Props:
 *   @param {string}  logoSrc          — Logo image path (default: /vukz-logo.jpeg)
 *   @param {string}  title            — Page title (required)
 *   @param {node}    subtitle         — Subtitle text or JSX
 *   @param {node}    actions          — CTA buttons (right side, bottom row on mobile)
 *   @param {string}  badgeText        — Optional badge label (e.g. "NATAÇÃO", "GESTÃO")
 *   @param {node}    badgeIcon        — Optional icon/dot inside badge
 *   @param {node}    rightSlot        — Filters, selects, toolbar (right side)
 *   @param {node}    secondaryContent — Extra row below header (filters bar, tabs, etc.)
 *   @param {string}  className        — Additional CSS classes on outer wrapper
 */
export default function PageHeader({
  logoSrc = '/vukz-logo.jpeg',
  title,
  subtitle,
  actions,
  badgeText,
  badgeIcon,
  rightSlot,
  secondaryContent,
  className = '',
}) {
  return (
    <div
      className={`border-b bg-white ${className}`}
      style={{ borderColor: VUKZ.border }}
    >
      <div className="mx-auto w-full max-w-7xl px-6 py-4">
        <div className="flex items-start sm:items-center justify-between gap-4 flex-wrap">
          {/* Left: Logo + Title */}
          <div className="flex items-center gap-4 min-w-0">
            <img
              src={logoSrc}
              alt="VUKZ"
              className="h-10 w-auto object-contain flex-shrink-0"
              onError={(e) => { e.target.style.display = 'none'; }}
            />

            {title && (
              <>
                {/* Divider */}
                <div className="h-8 w-px flex-shrink-0" style={{ backgroundColor: VUKZ.border }} />

                <div className="min-w-0">
                  <h1
                    className="text-lg font-semibold leading-tight truncate"
                    style={{ color: VUKZ.text }}
                  >
                    {title}
                  </h1>

                  {subtitle && (
                    <p
                      className="text-xs leading-tight mt-0.5"
                      style={{ color: VUKZ.textLight }}
                    >
                      {subtitle}
                    </p>
                  )}

                  {/* Badge */}
                  {badgeText && (
                    <span
                      className="inline-flex items-center gap-1.5 mt-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide"
                      style={{ backgroundColor: VUKZ.primaryLight, color: VUKZ.text }}
                    >
                      {badgeIcon && (
                        <span
                          className="inline-block w-1.5 h-1.5 rounded-full"
                          style={{ backgroundColor: VUKZ.text }}
                        />
                      )}
                      {badgeText}
                    </span>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Right: actions + rightSlot */}
          {(actions || rightSlot) && (
            <div className="flex shrink-0 items-center gap-3">
              {rightSlot}
              {actions}
            </div>
          )}
        </div>

        {/* Secondary content row (optional) */}
        {secondaryContent && (
          <div className="mt-4 pt-3 border-t" style={{ borderColor: VUKZ.border }}>
            {secondaryContent}
          </div>
        )}
      </div>
    </div>
  );
}
